import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useMemo } from 'react';

import { useHasRole } from '../auth';

import type { LucideIcon } from '@music-vine/cadence/icons';
import { Download, LockKeyhole } from '@music-vine/cadence/icons';

/**
 * Hook for the A/B test experiment (DEV-4111) that shows different CTAs
 * for free vs premium assets.
 *
 * When enabled:
 * - Free assets: Grey "Download" button
 * - Premium assets: Pink "Subscribe" button
 *
 * This experiment only targets visitors and free users.
 * Subscribers always see the standard pink "Download" button.
 *
 * Note: Growthbook's useFeatureIsOn gracefully returns false if not initialized,
 * so this hook safely defaults to control group behavior on any initialization issues.
 *
 * @returns boolean indicating if the explicit CTA experiment is active
 */
export const useExplicitCtaExperiment = (): boolean => {
  const isFree = useHasRole('Free');
  const isFeatureOn = useFeatureIsOn('explicit-cta-experiment');

  return isFeatureOn && isFree;
};

interface ExperimentButtonConfig {
  icon: LucideIcon;
  text: string;
  variant: 'brand' | 'light';
}

/**
 * Hook that returns the appropriate button configuration based on experiment state
 * and whether the asset is premium.
 *
 * @param assetIsPremium - Whether the asset requires a premium subscription
 * @param isExperiment - Whether the explicit CTA experiment is active
 * @returns Button configuration with variant, text, and icon
 */
export const useExperimentButton = (
  assetIsPremium: boolean,
  isExperiment: boolean
): ExperimentButtonConfig =>
  useMemo(() => {
    if (!isExperiment) {
      return { variant: 'brand', text: 'Download', icon: Download };
    }
    return assetIsPremium
      ? { variant: 'brand', text: 'Subscribe', icon: LockKeyhole }
      : { variant: 'light', text: 'Download', icon: Download };
  }, [assetIsPremium, isExperiment]);
